// Ayuda contextual del módulo Inventario. Inventario → Resumen → Ayuda.
// Patrón ayuda-de-modulo: checklist en vivo + flujo + ¿cómo hago...? + glosario.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BookOpen, Calculator, CheckCircle2, ChevronDown, ChevronRight, Hash, LifeBuoy,
  Lightbulb, Link2, ListChecks, Loader2, Sparkles, XCircle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { useTabsStore } from '@/stores/tabs.store';
import { invRpc, money, num } from './shared';

interface AyudaData {
  checklist: Record<string, boolean>;
  estado: { movs_mes: number; ultimo_doc: string | null; valorizado: number; lentos_n?: number; lentos_capital?: number };
  precios?: {
    alertas_abiertas: number; impacto_abierto: number; rojas: number;
    sin_acordado: number; sin_volatilidad: number;
  };
}

function useIr() {
  const openTab = useTabsStore((s) => s.openTab);
  return (path: string, title: string) => openTab({ id: path, title, path });
}

const CHECKS: { key: string; label: string; ruta: string; titulo: string; ayuda: string }[] = [
  { key: 'bodegas', label: 'Bodegas creadas', ruta: '/inventario/bodegas', titulo: 'Bodegas', ayuda: 'Crea al menos una bodega donde vivirá el stock.' },
  { key: 'bodega_principal', label: 'Bodega principal definida', ruta: '/inventario/bodegas', titulo: 'Bodegas', ayuda: 'Marca una bodega como principal: es el destino por defecto de las recepciones.' },
  { key: 'categorias', label: 'Categorías de productos', ruta: '/inventario/categorias', titulo: 'Categorías', ayuda: 'Las familias organizan el catálogo y definen atributos (color, talla).' },
  { key: 'productos', label: 'Catálogo de productos', ruta: '/inventario/productos', titulo: 'Productos', ayuda: 'Crea los productos; si el modelo tiene color/talla, genera sus variantes.' },
  { key: 'lista_precios', label: 'Lista de precios activa', ruta: '/inventario/listas-precios', titulo: 'Listas de precios', ayuda: 'El POS y facturación resuelven el precio desde una lista de venta.' },
  { key: 'niveles_wms', label: 'Esquema de ubicaciones (WMS)', ruta: '/inventario/ubicaciones', titulo: 'Ubicaciones', ayuda: 'Opcional: define los niveles (zona/pasillo/percha/nivel) de tus bodegas.' },
  { key: 'ubicaciones', label: 'Ubicaciones (bins) generadas', ruta: '/inventario/ubicaciones', titulo: 'Ubicaciones', ayuda: 'Opcional: genera los bins para ubicar el stock dentro de la bodega.' },
  { key: 'stock_inicial', label: 'Stock inicial cargado', ruta: '/inventario/carga-inicial', titulo: 'Carga inicial', ayuda: 'Carga las existencias iniciales o recibe tu primera compra.' },
  { key: 'cuentas_contables', label: 'Cuentas contables mapeadas', ruta: '/contabilidad/configuracion', titulo: 'Configuración contable', ayuda: 'Mapea Inventario y Costo de ventas para la contabilización automática.' },
  { key: 'reposicion', label: 'Mínimos de reposición calculados', ruta: '/inventario/reposicion', titulo: 'Reposición', ayuda: 'Abre Reposición una vez: el motor calcula mínimos/máximos desde tu venta real y se mantienen solos.' },
  { key: 'abc', label: 'Clasificación ABC calculada', ruta: '/inventario/abc-productos', titulo: 'Clasificación ABC', ayuda: 'Corre la clasificación ABC-XYZ: prioriza la reposición, muestra el top al vender y detecta los lentos para liquidar.' },
  { key: 'precio_control', label: 'Control de precios de compra configurado', ruta: '/inventario/precios-compra', titulo: 'Precios de compra', ayuda: 'Define los umbrales por clase de volatilidad: es lo que avisa cuando un proveedor sube el precio sin decirlo.' },
  { key: 'precio_acordado', label: 'Precios acordados con proveedores', ruta: '/inventario/precios-compra', titulo: 'Precios de compra', ayuda: 'El precio negociado es el mejor punto de comparación. Se fija con un clic desde la propia recepción.' },
  { key: 'precio_volatilidad', label: 'Clase de volatilidad marcada en los productos', ruta: '/inventario/productos', titulo: 'Productos', ayuda: 'Alta (hoja verde, tomate), media (papa, pollo), baja (aceite, abarrote). Sin esto, todos alertan al 12% y se genera ruido.' },
];

const FLUJO = [
  { n: 1, titulo: 'Compra al proveedor', detalle: 'La factura llega por Gestión SRI (Financiero) y se clasifica.', ruta: '/sri/procesar', tituloTab: 'Procesar Recibidos' },
  { n: 2, titulo: 'Recepción de mercadería', detalle: 'Recibe la factura en bodega: crea lote, ubica en bins y actualiza kardex + costo promedio. El control de precios avisa en el momento si el proveedor subió sin decirlo — nunca frena el descargue.', ruta: '/inventario/recepcion', tituloTab: 'Recepción' },
  { n: 2.5, titulo: 'Revisión de desviaciones de precio', detalle: 'Compras revisa las líneas que llegaron fuera de lo acordado: acepta con motivo (y fija el nuevo precio) o rechaza para reclamar al proveedor.', ruta: '/inventario/precios-compra', tituloTab: 'Precios de compra' },
  { n: 3, titulo: 'Producción (opcional)', detalle: 'Si fabricas: la OT consume materia prima y entrega producto terminado.', ruta: '/produccion/ordenes', tituloTab: 'Órdenes de Trabajo' },
  { n: 4, titulo: 'Transferencias', detalle: 'Reparte stock entre bodegas (p.ej. bodega → exhibición).', ruta: '/inventario/transferencias', tituloTab: 'Transferencias' },
  { n: 5, titulo: 'Venta', detalle: 'El POS descuenta stock por bin automáticamente y contabiliza el costo.', ruta: '/pos/venta', tituloTab: 'Venta POS' },
  { n: 6, titulo: 'Reposición', detalle: 'El motor detecta lo que está bajo mínimo y sugiere: transferir del excedente de otra bodega o generar la orden de compra al proveedor habitual.', ruta: '/inventario/reposicion', tituloTab: 'Reposición' },
  { n: 7, titulo: 'Control', detalle: 'Kardex, valorización, caducidades y stock por ubicación para auditar el ciclo.', ruta: '/inventario/kardex', tituloTab: 'Kardex' },
  { n: 8, titulo: 'Clasificación ABC y liquidación', detalle: 'La clasificación ABC-XYZ prioriza qué reponer, marca el top al vender y detecta los lentos: transfiérelos al local donde rotan o liquídalos por caducidad con rebajas escalonadas.', ruta: '/inventario/abc-productos', tituloTab: 'Clasificación ABC' },
];

const COMO: { q: string; pasos: string[]; enlaces: { to: string; t: string }[] }[] = [
  {
    q: '¿Cómo creo un producto con tallas y colores?',
    pasos: [
      'Verifica que la categoría tenga los atributos COLOR y TALLA (pestaña Plantilla en Atributos).',
      'Crea el producto padre (modelo) en Productos.',
      'En la pestaña Variantes del producto, elige colores y tallas y pulsa Generar.',
      'Cada variante recibe su propio SKU, código de barras, precio y stock.',
    ],
    enlaces: [{ to: '/inventario/atributos', t: 'Atributos' }, { to: '/inventario/productos', t: 'Productos' }],
  },
  {
    q: '¿Cómo recibo una compra con lote y ubicación?',
    pasos: [
      'En Recepción, selecciona la factura de compra ya clasificada.',
      'El sistema empareja las líneas con el catálogo (por código o mapeo del proveedor).',
      'Indica el número de lote (partida) y el bin de destino por línea.',
      'Confirma: se actualizan kardex, costo promedio y la contabilidad automática.',
    ],
    enlaces: [{ to: '/inventario/recepcion', t: 'Recepción' }],
  },
  {
    q: '¿Cómo fabrico producto terminado desde telas (producción)?',
    pasos: [
      'Define la receta (BOM) del modelo y las tarifas MO/CIF en Recetas y costos.',
      'Crea una Orden de Trabajo con las variantes y cantidades a fabricar.',
      'Confírmala: consume la materia prima de la bodega MP (a Productos en Proceso).',
      'Registra las entradas de producto terminado (pueden ser parciales); el lote es el número de la OT.',
      'Cierra la orden: el sobrante de proceso se liquida como merma.',
    ],
    enlaces: [{ to: '/produccion/recetas', t: 'Recetas y costos' }, { to: '/produccion/ordenes', t: 'Órdenes de Trabajo' }],
  },
  {
    q: '¿Cómo muevo stock entre bodegas o entre bins?',
    pasos: [
      'Entre bodegas: usa Transferencias (elige lote y bin de origen/destino).',
      'Dentro de la misma bodega: usa Reubicación (solo cambia el bin, no el costo).',
    ],
    enlaces: [{ to: '/inventario/transferencias', t: 'Transferencias' }, { to: '/inventario/reubicacion', t: 'Reubicación' }],
  },
  {
    q: '¿Cómo funciona una transferencia EN TRÁNSITO (envío → recepción)?',
    pasos: [
      'Actívalo en Transferencias → pestaña «Tránsito y diferencias» → Configuración → «Requiere recepción en destino». Sin activar, la transferencia es instantánea (el stock aparece en el destino al confirmar, como siempre).',
      'Con el flujo activo, «Enviar» descuenta del origen y la mercadería queda EN TRÁNSITO (una bodega virtual del sistema): todavía NO existe en el destino.',
      'El destino ve un badge en «Recepciones pendientes». Al llegar el camión, «Recibir»: cuenta lo que llegó (a ciegas, sin ver lo enviado; puede escanear el código de barras — cada bip suma, con «× por bip» para bultos).',
      'Al «Confirmar recepción»: lo contado entra al stock del destino DE INMEDIATO (ya es vendible); lo que falte queda como DIFERENCIA por resolver, sin bloquear el resto.',
      'Ejemplo: envías 10, llegan 7 con 3 cajas dañadas. Recibes contando 7 (entran a percha), marcas motivo «Dañado» en las 3, y un administrador las resuelve luego (merma del origen, cargo al transportista, o «apareció» si un reconteo las encuentra).',
      'Si el camión regresó o nunca salió: «Retornar» devuelve el saldo al origen. Un tránsito que nadie recibe se puede «Cerrar» (forzado, solo admin, con motivo).',
      'Guardarraíl farmacia: no se envían lotes que caducan antes de la fecha estimada de llegada + margen.',
    ],
    enlaces: [{ to: '/inventario/transferencias', t: 'Transferencias' }],
  },
  {
    q: '¿Cómo emito la guía de remisión SRI al transferir entre locales?',
    pasos: [
      'En Ecuador el traslado de mercadería entre establecimientos del mismo RUC exige guía de remisión electrónica (código 06), aunque no haya venta (Reglamento de Comprobantes, Art. 28).',
      'Configúralo una vez: Transferencias → Tránsito y diferencias → Configuración → activa «Emitir guía de remisión SRI». Y en Inventario → Bodegas pon el código de establecimiento SRI (001, 002…) de cada bodega.',
      'Al enviar una transferencia, en la pestaña «Tránsito y diferencias» cada tránsito abierto muestra un botón «Emitir guía»: eliges el transportista (o transporte propio), la placa y el motivo del traslado.',
      'La guía se emite al SRI en segundo plano (no bloquea el envío; el camión puede salir). La columna «Guía SRI» muestra el estado: Encolada → Autorizada; si el SRI la rechaza, botón «Reintentar».',
      'Cuando está Autorizada, el ícono de documento abre el RIDE (PDF) que debe viajar con el vehículo.',
      'Requiere que la empresa tenga configurado su certificado de firma (.p12) y ambiente SRI, igual que la facturación.',
    ],
    enlaces: [{ to: '/inventario/transferencias', t: 'Transferencias' }, { to: '/inventario/bodegas', t: 'Bodegas' }],
  },
  {
    q: '¿Cómo hago una toma física (conteo de bodega)?',
    pasos: [
      'Operaciones → Toma física → Nuevo conteo. Eliges la bodega y el alcance: Parcial (una categoría o una clase ABC), Total (toda la bodega, incluidos los que el sistema cree en cero) o Cíclico.',
      'Al abrirlo se CONGELA el saldo del sistema en la hoja. A partir de ahí el conteo vive de esa foto: lo que se venda mientras cuentas NO se convertirá en faltante.',
      'Deja marcado «Contar a ciegas» (viene activado): quien cuenta no ve la cantidad del sistema. Es lo único que hace fiable un conteo — si se ve el número, se «encuentra» el número.',
      'Escribe las cantidades y Guardar. Una línea en blanco no es un cero: si el producto no estaba, escribe 0.',
      'Revisar: las líneas que difieren quedan marcadas para RECONTAR. Cuenta esas otra vez; el segundo conteo manda. Así se separa el error de conteo del faltante real.',
      'Cerrar y ajustar: primero ves una simulación con el faltante y el sobrante valorados. Al confirmar se genera el ajuste con la DIFERENCIA (no con el número contado) y el acta queda sellada con su hash y su responsable. Un conteo cerrado ya no se anula.',
    ],
    enlaces: [{ to: '/inventario/conteos', t: 'Toma física' }],
  },
  {
    q: '¿Qué es el conteo cíclico y por qué es mejor que contar todo una vez al año?',
    pasos: [
      'Parar la bodega un domingo al año para contar tres mil productos se cuenta mal, cansado y tarde. El conteo cíclico cuenta un puñado cada día.',
      'En Nuevo conteo elige tipo «Cíclico» y la bodega: el sistema propone qué toca contar HOY usando la clasificación ABC que ya calcula.',
      'La cadencia: los A cada 30 días, los B cada 90 y los C cada 365. Se prioriza lo que más se ha pasado de su plazo, y a igualdad, lo de mayor valor.',
      '«Tomar los N» y abres el conteo con esa lista. Al cerrarlo, esos productos reinician su plazo.',
    ],
    enlaces: [{ to: '/inventario/conteos', t: 'Toma física' }, { to: '/inventario/abc-productos', t: 'Clasificación ABC' }],
  },
  {
    q: '¿Cómo doy de baja lo que ya venció?',
    pasos: [
      'Reportes → Caducidades → pestaña «Dar de baja»: lista los lotes cuya fecha ya pasó, con sus unidades y lo que valen.',
      'Marca los que vas a destruir y pulsa «Dar de baja». Verás primero cuánto suma; al confirmar se genera un documento de ajuste con motivo «Producto caducado» y el valor va a la cuenta de merma, con tu nombre.',
      'Una baja es un documento por bodega: si seleccionas lotes de dos bodegas, hazlo en dos pasos.',
      'Lo importante mientras tanto: el sistema NO deja que la caja despache un lote vencido. Antes lo hacía —FEFO entrega primero lo que antes expira, y un vencido es justo eso— y ahora los vencidos quedan bloqueados hasta que se den de baja.',
      'Si necesitas desactivar ese bloqueo para tu empresa, es una decisión de nivel 5 y queda registrada.',
    ],
    enlaces: [{ to: '/inventario/caducidades', t: 'Caducidades' }],
  },
  {
    q: '¿Cómo aparto stock para un cliente o un pedido?',
    pasos: [
      'Operaciones → Reservas → «Reservar». Eliges bodega, producto y cantidad, y verás tres números: lo que hay en bodega, lo ya reservado y lo realmente DISPONIBLE. Sólo puedes apartar de lo disponible.',
      'Una reserva NO mueve stock: el producto sigue en bodega y en el kardex. Lo que cambia es que deja de poder prometerse a otro — si otra venta intenta llevárselo, el sistema la frena y dice cuántas están reservadas.',
      'Toda reserva caduca (7 días por defecto). Sin caducidad, una cotización que nadie acepta apartaría el producto para siempre.',
      'Al despachar el pedido, la reserva se consume; si el cliente se cae, «Liberar» la devuelve al disponible.',
    ],
    enlaces: [{ to: '/inventario/reservas', t: 'Reservas' }],
  },
  {
    q: '¿Por qué debería cerrar el período de inventario?',
    pasos: [
      'Porque el costo promedio es ACUMULATIVO: un movimiento con fecha vieja recalcula el costo de todo lo que vino después y cambia el costo de ventas de meses que ya declaraste — sin tocar un solo asiento del mes que contabilidad selló.',
      'Operaciones → Cierre de período: elige el año y cierra los meses terminados, del más antiguo al más reciente.',
      'Al cerrar se congela la valorización producto por producto y bodega por bodega, con su hash. «Ver foto» te la muestra junto al valor de hoy.',
      'Si hay movimientos del mes con valor y sin asiento contable, el cierre se niega: sellar encima de eso escondería el descuadre. Resuélvelos primero en Contabilidad → Excepciones.',
      'Reabrir un mes se puede, pero exige nivel 5 y un motivo escrito que queda en el acta. Y hay que reabrir del más reciente hacia atrás.',
    ],
    enlaces: [{ to: '/inventario/cierre-periodo', t: 'Cierre de período' }],
  },
  {
    q: '¿Cómo sé de qué compra salió lo que tengo en bodega?',
    pasos: [
      'Reportes → Costos → «Capas de costo». Eliges el producto y ves cada entrada viva: cuándo entró, en qué documento, de qué proveedor, cuántas quedan y a qué costo.',
      'Así puedes responder «de estas 400, 300 costaron 1,20 y 100 costaron 1,45», que es lo que hace falta para fijar el PVP y para negociar la siguiente compra.',
      'La pestaña «Promedio vs. realidad» ordena los productos por cuánto se separa el valor del balance de lo que realmente costó lo que queda. Una diferencia grande y persistente significa que el precio de compra se movió y el promedio lo está disimulando.',
      'IMPORTANTE: esto NO cambia el método de valoración. El balance y el costo de ventas siguen usando promedio ponderado; las capas son el detalle que el promedio esconde.',
      'La pestaña «Pendientes de costear» lista lo que está en bodega valorado en cero (o casi), que el balance no cuenta y cuya venta reporta 100 % de margen inventado. Desde ahí se recostea con su asiento de ajuste.',
    ],
    enlaces: [{ to: '/inventario/costos', t: 'Costos' }],
  },
  {
    q: '¿Y si sólo quiero corregir una cantidad puntual, sin contar?',
    pasos: [
      'En Ajustes crea un documento con las diferencias (positivas o negativas).',
      'Confírmalo: el kardex y la contabilidad (sobrante/merma) se actualizan solos.',
      'Para cuadrar una bodega entera usa la Toma física, no Ajustes: el ajuste es el resultado, la toma física es el procedimiento que lo justifica ante una auditoría.',
    ],
    enlaces: [{ to: '/inventario/ajustes', t: 'Ajustes' }, { to: '/inventario/conteos', t: 'Toma física' }],
  },
  {
    q: '¿Cómo funcionan los mínimos y la reposición?',
    pasos: [
      'Los mínimos y máximos son DINÁMICOS: el motor los calcula por producto y bodega desde tu venta real (últimos 90 días) y el tiempo de entrega de cada proveedor. No hay que mantenerlos a mano.',
      'Se recalculan solos al abrir Reposición (si el dato tiene más de 24 horas) o con el botón "Recalcular ahora".',
      'Si quieres fijar un mínimo a mano, edítalo y queda con CANDADO 🔒: el motor lo respeta hasta que lo liberes.',
      'La pestaña Sugerencias muestra lo que está bajo mínimo con su COBERTURA EN DÍAS (🔴 ≤3 · 🟡 ≤7 · 🟢 sano) y de dónde reponer.',
      'Si otra bodega tiene excedente, el botón azul transfiere con un clic; si no, el sistema sugiere al proveedor habitual con su último costo.',
      'El buscador del POS (F1) pinta el stock con el mismo semáforo — cajero y administrador ven el mismo dato.',
      'Los parámetros (ventana, lead time, seguridad, cobertura) se ajustan en la pestaña Parámetros.',
    ],
    enlaces: [{ to: '/inventario/reposicion', t: 'Reposición' }],
  },
  {
    q: '¿Cómo genero órdenes de compra y las recibo?',
    pasos: [
      'En Reposición → Sugerencias, marca las filas a comprar y pulsa "Generar OC": se crea una orden en BORRADOR por proveedor y bodega, con cantidades sugeridas y últimos costos.',
      'En la pestaña Órdenes de compra revisa y APRUEBA la orden (o anúlala con motivo).',
      'Exporta la lista a XLS/PDF para enviarla al proveedor.',
      'Cuando llegue la mercadería: en Recepción usa "Orden de compra (precargar pendiente)" — las líneas se llenan con lo que falta por recibir.',
      'Al confirmar la recepción, la orden acumula lo recibido y pasa a Recibida parcial o Recibida automáticamente (acepta varias entregas).',
    ],
    enlaces: [{ to: '/inventario/reposicion', t: 'Reposición' }, { to: '/inventario/recepcion', t: 'Recepción' }],
  },
  {
    q: '¿Cómo clasifico mis productos por rotación (ABC-XYZ)?',
    pasos: [
      'En Clasificación ABC define los pesos del índice (ingreso, unidades, margen), la ventana de análisis (90 días) y los umbrales A/B; puedes overridear la configuración por local.',
      'Pulsa "Recalcular ahora": el motor rankea cada producto POR LOCAL y a nivel EMPRESA (Pareto acumulado → clases A/B/C/D) y calcula la estabilidad de la demanda (XYZ) y la cobertura en días.',
      'Clase A = lo que más aporta; D = sin ventas. XYZ: X estable, Z errático. es_lento = clase C/D con sobre-stock.',
      'El resultado se usa solo: badge del top en el buscador del POS (F1), prioridad en Reposición, y escena de "lentos" en Signage.',
      'El worker lo refresca de forma automática; el botón es para forzarlo.',
    ],
    enlaces: [{ to: '/inventario/abc-productos', t: 'Clasificación ABC' }],
  },
  {
    q: '¿Cómo saco los productos que se mueven lento (dead-stock)?',
    pasos: [
      'En Reposición → pestaña "Lentos" ves el capital inmovilizado en productos de lento movimiento por bodega.',
      'Si el producto es A/B en otro local, el botón "Transferir →" lo manda allí en 1 clic (recupera precio completo).',
      'Si no rota en ningún local, la acción sugerida es Liquidar / promocionar.',
      'Los productos regulados, esenciales, de temporada o nuevos salen como "Revisar": su baja rotación es esperada y no se liquidan automáticamente (marca esos flags en la ficha del producto).',
    ],
    enlaces: [{ to: '/inventario/reposicion', t: 'Reposición' }, { to: '/inventario/productos', t: 'Productos' }],
  },
  {
    q: '¿Cómo liquido productos por vencer con rebajas escalonadas (markdown)?',
    pasos: [
      'En Liquidación (markdown) define el esquema de tramos: mientras más cerca la caducidad, mayor descuento (p.ej. ≤15 días 50%, ≤30 días 35%…).',
      'Elige la bodega y revisa el preview: productos por vencer con el precio ya rebajado y el capital en riesgo.',
      'Pulsa "Aplicar markdown": crea la promoción real SOLO en esa bodega (afecta únicamente su POS) y el cajero cobra el precio rebajado.',
      'La misma escena "Por vencer" de Signage anuncia esas rebajas. "Deshacer" revierte las rebajas de la bodega.',
      'Los productos regulados quedan excluidos de la rebaja automática.',
    ],
    enlaces: [{ to: '/inventario/liquidacion', t: 'Liquidación (markdown)' }],
  },
  {
    q: '¿Dónde veo el costo y el valor de mi inventario?',
    pasos: [
      'Valorización muestra el stock a costo promedio por bodega.',
      'El Kardex detalla entradas/salidas y el costo de cada movimiento.',
      'Stock por ubicación muestra el detalle por bin (WMS).',
    ],
    enlaces: [{ to: '/inventario/valorizacion', t: 'Valorización' }, { to: '/inventario/kardex', t: 'Kardex' }, { to: '/inventario/stock-ubicacion', t: 'Stock por ubicación' }],
  },
  // ---- Control de precios de compra ----
  {
    q: 'PRECIOS · El proveedor subió el precio y no me avisó: ¿cómo me entero a tiempo?',
    pasos: [
      'Al teclear el costo en la Recepción, el campo se pinta ámbar o rojo si el precio se salió de lo acordado, con el % y el impacto en dólares de esa línea.',
      'Puedes recibir igual: la mercadería entra al inventario (nunca se frena el descargue con el camión en el muelle).',
      'La línea queda como alerta en Precios de compra → Alertas, con su impacto, para que compras la resuelva.',
      'Y lo más importante: mientras la desviación no se apruebe, el costo con que se costean las recetas y los productos NO se actualiza — un precio anómalo no contamina tus costos.',
    ],
    enlaces: [{ to: '/inventario/recepcion', t: 'Recepción' }, { to: '/inventario/precios-compra', t: 'Precios de compra' }],
  },
  {
    q: 'PRECIOS · Fijar el precio acordado con un proveedor',
    pasos: [
      'La forma rápida: en la Recepción, cuando la línea está en verde al precio correcto, pulsa «fijar como acordado» debajo del costo.',
      'La otra: al resolver una alerta, marca «Fijar este precio como el nuevo acordado» — así la excepción deja de ser excepción y la próxima compra ya no alerta.',
      'El precio acordado es el mejor punto de comparación: comparar solo contra «el último precio pagado» es un control que se esquiva subiendo 4% cada semana.',
    ],
    enlaces: [{ to: '/inventario/recepcion', t: 'Recepción' }, { to: '/inventario/precios-compra', t: 'Precios de compra' }],
  },
  {
    q: 'PRECIOS · Resolver una alerta: aceptar el alza o reclamar',
    pasos: [
      'En Precios de compra → Alertas, pulsa «Resolver» en la línea.',
      'Elige el motivo de la lista (escasez/clima, calibre, cambió la calidad, flete, error de lista): el motivo de texto libre no sirve para analizar después.',
      'Aceptar: registra quién aprobó y, si marcas la casilla, fija el nuevo precio acordado.',
      'Rechazar: la desviación queda documentada para reclamar al proveedor.',
      'Quien registró la recepción no puede aprobar su propia desviación: debe hacerlo compras o gerencia.',
    ],
    enlaces: [{ to: '/inventario/precios-compra', t: 'Precios de compra' }],
  },
  {
    q: 'PRECIOS · Las alertas son demasiadas (o ninguna): ¿cómo las calibro?',
    pasos: [
      'Marca la clase de volatilidad de cada producto en su ficha: alta (hoja verde, tomate, fruta), media (papa, arroz, pollo), baja (aceite, azúcar, abarrote).',
      'En perecibles un ±20% semanal es el mercado respirando; en aceite un 15% es un evento. Un umbral único para todo es lo que genera el ruido.',
      'Ajusta en Precios de compra → Umbrales: tolerancia por clase, el piso de materialidad (no alertar si el impacto es de centavos) y el tope en dólares que pinta la línea en rojo.',
      'Regla práctica: si un ítem alerta 3 semanas seguidas, el umbral está mal, no el proveedor.',
    ],
    enlaces: [{ to: '/inventario/precios-compra', t: 'Precios de compra' }, { to: '/inventario/productos', t: 'Productos' }],
  },
  {
    q: 'PRECIOS · Detectar al proveedor que sube “despacio”',
    pasos: [
      'Un proveedor que sube 3% cada semana nunca dispara una alerta puntual, pero en un trimestre te subió 40%.',
      'Abre Precios de compra → Tendencia: muestra la serie de las últimas semanas por producto y proveedor, con el acumulado y el alza promedio por semana.',
      'La pestaña Variación complementa: está ordenada por impacto en dólares, así arriba salen los ítems que mueven la plata (no los que tienen el % más alto).',
    ],
    enlaces: [{ to: '/inventario/precios-compra', t: 'Precios de compra' }],
  },
  {
    q: 'PRECIOS · ¿Es alza del mercado o de mi proveedor?',
    pasos: [
      'Compara la variación del mismo producto entre tus proveedores en la pestaña Variación.',
      'Si subieron los dos parecido, es alza de mercado: no es negociable, se gestiona en el menú o sustituyendo el ingrediente.',
      'Si subió uno y el otro no, es alza de ese proveedor: negociable, y ahora con evidencia en la mano.',
      'Ojo con lo que el precio no muestra: un saco que pasa de 100 a 92 libras al mismo precio es un alza encubierta del 8%, y una calidad inferior no cambia el precio pero sube la merma.',
    ],
    enlaces: [{ to: '/inventario/precios-compra', t: 'Precios de compra' }],
  },
];

const GLOSARIO: { t: string; d: string }[] = [
  { t: 'Variante (SKU)', d: 'Combinación concreta de modelo + color + talla. Es lo que se compra, almacena y vende; el modelo (padre) es solo la plantilla.' },
  { t: 'Lote / partida', d: 'Agrupación de unidades que entraron juntas (una compra o una orden de producción). Da trazabilidad: de qué compra/OT salió cada prenda.' },
  { t: 'Bin / ubicación', d: 'Posición física dentro de la bodega (zona-pasillo-percha-nivel). El stock se ubica y se pickea por bin.' },
  { t: 'Costo promedio ponderado', d: 'Método de costeo del sistema: cada entrada recalcula el costo promedio; las salidas usan ese promedio.' },
  { t: 'Kardex', d: 'Historial de movimientos de un producto (entradas, salidas, ajustes) con cantidades y costos.' },
  { t: 'Precio acordado', d: 'El precio negociado con un proveedor para un producto. Es el punto de comparación del control: si la factura se sale de ahí, no es «alza de mercado», es incumplimiento.' },
  { t: 'Clase de volatilidad', d: 'Cuánto se mueve legítimamente el precio de un producto: alta (hoja verde, tomate), media (papa, pollo), baja (aceite, abarrote). Define el umbral de su alerta.' },
  { t: 'Materialidad', d: 'Piso en dólares por debajo del cual no vale la pena alertar: el orégano que sube 30% son $1,20. Evita la fatiga de alertas.' },
  { t: 'Impacto (en dólares)', d: 'La desviación de precio multiplicada por la cantidad comprada. Es el orden correcto para leer el reporte: el % engaña, los dólares no.' },
  { t: 'Alza encubierta', d: 'Subir el precio sin tocar el precio: menos peso en el mismo bulto, calidad inferior (que sube la merma), cambio de unidad o flete cobrado aparte.' },
  { t: 'BOM / receta', d: 'Lista de materiales para fabricar 1 unidad de un modelo (tela, hilo, avíos), con factor por talla.' },
  { t: 'WIP / Productos en Proceso', d: 'Cuenta contable donde vive el costo de una orden de trabajo mientras se fabrica (MP + mano de obra + CIF + maquila).' },
  { t: 'Maquila', d: 'Confección encargada a un taller externo: tú pones la materia prima, el tercero factura el servicio y ese costo se suma a la prenda.' },
  { t: 'Mínimo dinámico', d: 'Stock mínimo por producto×bodega que el motor recalcula solo: venta diaria promedio × (lead time + días de seguridad). El máximo agrega la cobertura objetivo.' },
  { t: 'Stock de seguridad (SS)', d: 'El colchón de unidades para no quebrar mientras llega el pedido. Se calcula con la variabilidad de la demanda (σ), el nivel de servicio de la clase ABC (Z) y el tiempo de entrega: SS = Z · σ · √(lead time).' },
  { t: 'Punto de pedido / mínimo', d: 'El nivel de stock que dispara la compra o transferencia: cubre la demanda esperada durante el tiempo de entrega, más el stock de seguridad (μ · lead time + SS).' },
  { t: 'Censura de demanda', d: 'Corrección que excluye del cálculo los días en que el producto estuvo agotado: un día en quiebre no es "no se vendió", es "no se pudo vender". Contarlo haría que el sistema pida de menos justo lo que más rota.' },
  { t: 'Candado 🔒', d: 'Marca de mínimo manual: el motor de reposición nunca pisa esa fila hasta que la liberes.' },
  { t: 'Lead time', d: 'Días que tarda un proveedor en entregar; se calcula de tus recepciones reales (mediana) y admite ajuste manual por proveedor.' },
  { t: 'Cobertura', d: 'Días de venta que alcanza el stock actual al ritmo de demanda: es el semáforo de las sugerencias y del buscador del POS.' },
  { t: 'Orden de compra (OC)', d: 'Documento interno de compra: borrador → aprobada → recibida (parcial o total). La recepción se precarga desde la OC y la va cerrando.' },
  { t: 'Clasificación ABC', d: 'Ranking de productos por su aporte (índice combinado de ingreso + unidades + margen), calculado por LOCAL y a nivel EMPRESA: A = lo que más aporta … D = sin ventas. Distinta de la clasificación ABC de bodegas (que ordena sucursales).' },
  { t: 'XYZ', d: 'Estabilidad de la demanda de un producto: X = venta regular/predecible, Y = variable, Z = errática. Complementa la letra ABC.' },
  { t: 'Producto lento (dead-stock)', d: 'Clase C/D con exceso de cobertura (mucho stock para lo poco que rota): capital inmovilizado candidato a transferir o liquidar.' },
  { t: 'Markdown / liquidación', d: 'Rebaja escalonada por caducidad: cuanto más cerca vence el producto, mayor el descuento. Se aplica como promoción real por bodega y también alimenta la cartelería "Por vencer".' },
  { t: 'Histéresis', d: 'Amortiguación que evita el "baile de clases": suaviza el índice (EWMA) y conserva la clase previa dentro de una banda de frontera, para que un producto no cambie de A a B y vuelva por un pico de un día.' },
  { t: 'Flags del producto', d: 'Marcas que ajustan las recomendaciones: Regulado (receta/controlado — no liquidar), Esencial (no descontinuar), Estacional (baja rotación esperada fuera de temporada) y Nuevo (período de gracia sin historia de ventas).' },
];

const TABS: { key: 'guia' | 'demanda'; label: string }[] = [
  { key: 'guia', label: 'Guía del módulo' },
  { key: 'demanda', label: 'Demanda inteligente' },
];

const DEMANDA_USO: { titulo: string; detalle: string; enlaces?: { to: string; t: string }[] }[] = [
  {
    titulo: 'Se recalcula solo, cada noche',
    detalle: 'No hay que hacer nada. Si quieres verlo al instante, en Reposición hay un botón «Recalcular ahora».',
  },
  {
    titulo: 'Revisa las sugerencias',
    detalle: 'En Reposición → pestaña Sugerencias ves qué está bajo mínimo, la cobertura en días con semáforo (🔴 ≤3 · 🟡 ≤7 · 🟢 sano) y de dónde reponer: transferir el excedente de otra bodega o generar la Orden de Compra al proveedor habitual.',
    enlaces: [{ to: '/inventario/reposicion', t: 'Reposición' }],
  },
  {
    titulo: 'Ajustas política, no números',
    detalle: 'El nivel de servicio y la cobertura por clase ABC se cambian en la política ABC (pestaña Parámetros de Reposición). Nunca escribes un mínimo a mano… salvo que quieras: puedes fijar un mínimo/máximo manual con candado 🔒 y el sistema respeta ese valor y no lo recalcula.',
    enlaces: [{ to: '/inventario/reposicion', t: 'Reposición' }],
  },
  {
    titulo: 'La clasificación ABC alimenta la protección',
    detalle: 'Qué tan importante es cada producto define cuánta protección recibe (A se protege más, C menos). Revísala en Clasificación ABC.',
    enlaces: [{ to: '/inventario/abc-productos', t: 'Clasificación ABC' }],
  },
];

function Acordeon({ q, children }: { q: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border rounded-md">
      <button className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-left" onClick={() => setOpen((o) => !o)}>
        {q} {open ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
      </button>
      {open && <div className="px-3 pb-3 text-sm text-muted-foreground">{children}</div>}
    </div>
  );
}

export function AyudaInventario() {
  const ir = useIr();
  const [tab, setTab] = useState<'guia' | 'demanda'>('guia');
  const { data, isFetching } = useQuery<AyudaData>({
    queryKey: ['inv-ayuda-estado'],
    queryFn: () => invRpc<AyudaData>('inv_ayuda_estado'),
    staleTime: 0, refetchOnMount: 'always',
  });

  const Ir = ({ to, t }: { to: string; t: string }) => (
    <button className="inline-flex items-center gap-1 text-primary hover:underline" onClick={() => ir(to, t)}>
      <Link2 className="w-3.5 h-3.5" /> {t}
    </button>
  );

  return (
    <div className="p-6 space-y-4 max-w-5xl">
      <h1 className="text-xl font-semibold flex items-center gap-2">
        <LifeBuoy className="w-5 h-5" /> Ayuda — Inventario
        {isFetching && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
      </h1>

      <div className="flex gap-1 border-b">
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`px-3 py-1.5 text-sm font-medium border-b-2 -mb-px flex items-center gap-1.5 ${
              tab === t.key ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => setTab(t.key)}
          >
            {t.key === 'demanda' && <Sparkles className="w-3.5 h-3.5" />}
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'guia' && (
      <>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Listo para operar</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {CHECKS.map((c) => {
            const ok = data?.checklist?.[c.key] ?? false;
            return (
              <div key={c.key} className="flex items-start gap-2 text-sm">
                {ok ? <CheckCircle2 className="w-4 h-4 mt-0.5 text-green-600 shrink-0" /> : <XCircle className="w-4 h-4 mt-0.5 text-red-500 shrink-0" />}
                <div>
                  <span className={ok ? '' : 'font-medium'}>{c.label}</span>
                  <span className="text-muted-foreground"> — {c.ayuda}</span>{' '}
                  {!ok && <Ir to={c.ruta} t={c.titulo} />}
                </div>
              </div>
            );
          })}
          {data && (
            <div className="text-xs text-muted-foreground border-t pt-2 mt-2">
              Este mes: {num(data.estado.movs_mes, 0)} movimientos · Último documento: {data.estado.ultimo_doc ?? '—'} ·
              Inventario valorizado: <span className="font-medium">{money(data.estado.valorizado)}</span>
              {(data.estado.lentos_n ?? 0) > 0 && <> · Lento movimiento: <span className="font-medium">{num(data.estado.lentos_n ?? 0, 0)}</span> productos (<span className="font-medium">{money(data.estado.lentos_capital ?? 0)}</span> inmovilizado)</>}
            </div>
          )}
          {data?.precios && (
            <div className="text-xs border-t pt-2 mt-2">
              {data.precios.alertas_abiertas > 0 ? (
                <span className="text-amber-700 dark:text-amber-400">
                  Precios de compra: <span className="font-medium">{num(data.precios.alertas_abiertas, 0)}</span> desviación(es)
                  sin resolver por <span className="font-medium">{money(data.precios.impacto_abierto)}</span>
                  {data.precios.rojas > 0 && <> ({num(data.precios.rojas, 0)} en rojo)</>} — <Ir to="/inventario/precios-compra" t="Precios de compra" />
                </span>
              ) : (
                <span className="text-muted-foreground">Precios de compra: sin desviaciones pendientes.</span>
              )}
              {data.precios.sin_volatilidad > 0 && (
                <span className="block text-muted-foreground pt-0.5">
                  {num(data.precios.sin_volatilidad, 0)} producto(s) comprable(s) sin clase de volatilidad marcada:
                  todos alertan con el umbral medio. Marcarla es lo que evita el ruido.
                </span>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">El flujo del inventario</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {FLUJO.map((f) => (
            <div key={f.n} className="flex items-start gap-3 text-sm">
              <div className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold shrink-0">{f.n}</div>
              <div>
                <span className="font-medium">{f.titulo}.</span>{' '}
                <span className="text-muted-foreground">{f.detalle}</span>{' '}
                <Ir to={f.ruta} t={f.tituloTab} />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">¿Cómo hago…?</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {COMO.map((c) => (
            <Acordeon key={c.q} q={c.q}>
              <ol className="list-decimal ml-5 space-y-1">
                {c.pasos.map((p, i) => <li key={i}>{p}</li>)}
              </ol>
              <div className="flex flex-wrap gap-3 mt-2">
                {c.enlaces.map((e) => <Ir key={e.to} to={e.to} t={e.t} />)}
              </div>
            </Acordeon>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><BookOpen className="w-4 h-4" /> Glosario</CardTitle></CardHeader>
        <CardContent className="grid gap-2 md:grid-cols-2">
          {GLOSARIO.map((g) => (
            <div key={g.t} className="text-sm">
              <span className="font-medium">{g.t}:</span>{' '}
              <span className="text-muted-foreground">{g.d}</span>
            </div>
          ))}
        </CardContent>
      </Card>
      </>
      )}

      {tab === 'demanda' && (
      <>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><Lightbulb className="w-4 h-4" /> Qué es (en una frase)</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          El sistema mira la venta real de cada local y calcula solo cuánto stock debe tener y cuándo pedir —
          para no quebrar ni sobre-stockear. <span className="font-medium text-foreground">Nadie escribe mínimos a mano.</span>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><Calculator className="w-4 h-4" /> Cómo se calcula</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div>
            <span className="font-medium">Demanda diaria (μ):</span>{' '}
            <span className="text-muted-foreground">
              promedio por día de la semana (el fin de semana vende más que un martes), suavizado dando más peso a lo reciente.{' '}
              <span className="font-medium text-foreground">Corrección de censura:</span> los días que estuviste SIN stock NO cuentan
              — un día en quiebre no es "no se vendió", es "no se pudo vender"; incluirlos haría que el sistema pida de menos justo lo que más rota.
            </span>
          </div>
          <div>
            <span className="font-medium">Variabilidad (σ):</span>{' '}
            <span className="text-muted-foreground">qué tan parejo o errático vende el producto.</span>
          </div>
          <div>
            <span className="font-medium">Tiempo de entrega (lead time):</span>{' '}
            <span className="text-muted-foreground">
              días que tarda en llegar — de Principal a la sucursal (transferencia) o del proveedor a Principal (compra). Se mide del historial, no se pone a mano.
            </span>
          </div>
          <div>
            <span className="font-medium">Stock de seguridad (SS):</span>{' '}
            <span className="text-muted-foreground">
              el colchón para no quebrar mientras llega el pedido. Depende del nivel de servicio de la clase ABC (A se protege más, C menos).
            </span>
          </div>
          <pre className="text-xs md:text-sm font-mono bg-muted/50 rounded-md p-3 whitespace-pre overflow-x-auto">
{`Stock de seguridad   SS  = Z · σ · √(lead time)
Mínimo (punto de pedido) = μ · lead time + SS
Máximo (hasta dónde llenar) = Mínimo + μ · días de cobertura
Pedido sugerido = Máximo − (stock actual + lo que ya viene en tránsito)`}
          </pre>
          <div>
            <span className="font-medium">Banda muerta:</span>{' '}
            <span className="text-muted-foreground">si el número nuevo cambia poco respecto al anterior, no se toca (evita que los mínimos "bailen" cada día).</span>
          </div>
          <div>
            <span className="font-medium">Productos nuevos / sin historia:</span>{' '}
            <span className="text-muted-foreground">usan un método simple (promedio de ventas) hasta juntar ~2 semanas de datos; no se rompen.</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><Hash className="w-4 h-4" /> Ejemplo numérico</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <p className="text-muted-foreground">
            Agua 500 ml, clase A, en una sucursal. μ = 21 u/día, σ = 6, lead = 2 días, nivel de servicio A ⇒ Z = 2,05, cobertura objetivo 4 días:
          </p>
          <pre className="text-xs md:text-sm font-mono bg-muted/50 rounded-md p-3 whitespace-pre overflow-x-auto">
{`SS      = 2,05 · 6 · √2  ≈ 18 u
Mínimo  = 21 · 2 + 18    = 60 u    (aquí se dispara el pedido)
Máximo  = 60 + 21 · 4    = 144 u
Si hay 25 en la sucursal y 12 ya en tránsito:
Pedido sugerido = 144 − (25 + 12) = 107 u  → se redondea a caja`}
          </pre>
          <p className="text-muted-foreground">
            La idea clave: <span className="font-medium text-foreground">contar el tránsito evita pedir de más lo que ya viene en camino.</span>
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><ListChecks className="w-4 h-4" /> Cómo lo usa el usuario final</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {DEMANDA_USO.map((d, i) => (
            <div key={d.titulo} className="flex items-start gap-3 text-sm">
              <div className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold shrink-0">{i + 1}</div>
              <div>
                <span className="font-medium">{d.titulo}.</span>{' '}
                <span className="text-muted-foreground">{d.detalle}</span>{' '}
                {d.enlaces?.map((e) => <Ir key={e.to} to={e.to} t={e.t} />)}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
      </>
      )}
    </div>
  );
}
